import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getAidModels,
  getAidModel,
  createAidModel,
  updateAidModel,
  createAidModelCopy,
  renameAidModel,
  updateAidModelArchiveState,
} from 'api/aid-models/aid-models.api';
import type { AidModelsListParams } from 'api/aid-models/aid-models.api';
import type { AidModelDto } from 'api/generated/schemas/aidModelDto';
import type { AidModelCopyRequest } from 'api/generated/schemas/aidModelCopyRequest';
import type { AidModelArchiveStateRequest } from 'api/generated/schemas/aidModelArchiveStateRequest';
import type { AidModelRenameRequest } from 'api/generated/schemas/aidModelRenameRequest';

type ClientId = string | number;
type ModelId = string | number;

interface ListParams {
  userId: string;
  clientId: ClientId;
  queryParams?: AidModelsListParams;
  enabled?: boolean;
}

interface DetailParams {
  userId: string;
  clientId: ClientId;
  modelId: ModelId;
  enabled?: boolean;
}

interface MutationParams {
  userId: string;
  clientId: ClientId;
}

interface UpdateVariables {
  modelId: ModelId;
  data: unknown;
}

interface CopyVariables {
  sourceModelId: ModelId;
  data: AidModelCopyRequest;
}

interface RenameVariables {
  modelId: ModelId;
  data: AidModelRenameRequest;
}

interface ArchiveStateVariables {
  modelId: ModelId;
  data: AidModelArchiveStateRequest;
}

export const aidModelsKeys = {
  all: ['aid-models'] as const,
  lists: (): readonly unknown[] => [...aidModelsKeys.all, 'list'],
  list: (
    clientId: ClientId,
    params: AidModelsListParams | undefined
  ): readonly unknown[] => [...aidModelsKeys.lists(), clientId, params],
  details: (): readonly unknown[] => [...aidModelsKeys.all, 'detail'],
  detail: (clientId: ClientId, modelId: ModelId): readonly unknown[] => [
    ...aidModelsKeys.details(),
    clientId,
    modelId,
  ],
};

export const useAidModelsList = ({
  userId,
  clientId,
  queryParams,
  enabled = true,
}: ListParams) =>
  useQuery({
    queryKey: aidModelsKeys.list(clientId, queryParams),
    queryFn: () => getAidModels(userId, clientId, queryParams),
    enabled: enabled && !!userId && !!clientId,
    staleTime: 30000,
    placeholderData: (previousData) => previousData,
  });

export const useAidModel = ({
  userId,
  clientId,
  modelId,
  enabled = true,
}: DetailParams) =>
  useQuery({
    queryKey: aidModelsKeys.detail(clientId, modelId),
    queryFn: () => getAidModel(userId, clientId, modelId),
    enabled: enabled && !!userId && !!clientId && !!modelId,
    staleTime: 60000,
  });

export const useCreateAidModel = ({ userId, clientId }: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: unknown) => createAidModel(userId, clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aidModelsKeys.lists() });
    },
  });
};

export const useUpdateAidModel = ({ userId, clientId }: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, data }: UpdateVariables) =>
      updateAidModel(userId, clientId, modelId, data),
    onSuccess: (updatedModel, variables) => {
      queryClient.setQueryData(
        aidModelsKeys.detail(clientId, variables.modelId),
        updatedModel
      );
      queryClient.invalidateQueries({ queryKey: aidModelsKeys.lists() });
    },
  });
};

export const useCopyAidModel = ({ userId, clientId }: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation<AidModelDto, unknown, CopyVariables>({
    mutationFn: ({ sourceModelId, data }: CopyVariables) =>
      createAidModelCopy(userId, clientId, sourceModelId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aidModelsKeys.lists() });
    },
  });
};

export const useRenameAidModel = ({ userId, clientId }: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation<AidModelDto, unknown, RenameVariables>({
    mutationFn: ({ modelId, data }: RenameVariables) =>
      renameAidModel(userId, clientId, modelId, data),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData(
        aidModelsKeys.detail(clientId, variables.modelId),
        updated
      );
      queryClient.invalidateQueries({ queryKey: aidModelsKeys.lists() });
    },
  });
};

export const useUpdateAidModelArchiveState = ({
  userId,
  clientId,
}: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation<AidModelDto, unknown, ArchiveStateVariables>({
    mutationFn: ({ modelId, data }: ArchiveStateVariables) =>
      updateAidModelArchiveState(userId, clientId, modelId, data),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData(
        aidModelsKeys.detail(clientId, variables.modelId),
        updated
      );
      queryClient.invalidateQueries({ queryKey: aidModelsKeys.lists() });
    },
  });
};
